import React, { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import setInputSelection from 'app/common/lib/setInputSelection'

import styles from './slot-name-input.module.scss'


type Props = {
  id: string
  value: string
  hasError?: boolean
  onChange: (val: string) => string | null
  onClearError?: () => void
  onBeginEdit: (id: string) => void
  onStopEdit: (id: string) => void
}

export const SlotNameInput = ({
  id,
  value,
  hasError,
  onChange,
  onClearError,
  onBeginEdit,
  onStopEdit,
}: Props) => {
  const [localValue, setLocalValue] = useState(value || '')
  const savedValue = useRef(value || '')
  const resetOnBlur = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const skipSelectionOnFocus = useRef(false)

  useEffect(() => {
    const nextValue = value || ''
    setLocalValue(nextValue)
    savedValue.current = nextValue
  }, [value])

  const commitValue = useCallback(() => {
    if (savedValue.current !== localValue) {
      const commited = onChange(localValue)
      if (commited === null) {
        return false
      }
      setLocalValue(commited)
    }
    return true
  }, [onChange, localValue])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasError && onClearError) {
      onClearError()
    }
    setLocalValue(e.currentTarget.value)
  }, [hasError, onClearError])

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    const { value: v } = input
    onBeginEdit(id)
    if (skipSelectionOnFocus.current) {
      skipSelectionOnFocus.current = false
      setInputSelection(v.length, v.length, input)
      return
    }
    setInputSelection(0, v.length, input)
  }, [onBeginEdit, id])

  const handleBlur = useCallback(() => {
    if (resetOnBlur.current) {
      onStopEdit(id)
      setLocalValue(savedValue.current)
      resetOnBlur.current = false
      return
    }
    if (!commitValue()) {
      window.setTimeout(() => {
        if (inputRef.current) {
          skipSelectionOnFocus.current = true
          inputRef.current.focus()
        }
      }, 0)
      return
    }
    onStopEdit(id)
  }, [onStopEdit, commitValue, id])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e
    const input = e.currentTarget
    if (key === 'Escape') {
      resetOnBlur.current = true
      input.blur()
    }
    if (key === 'Enter') {
      input.blur()
    }
  }, [])

  const elementId = `plItemSlotName${id}`

  return (
    <label className={styles.root} htmlFor={elementId}>
      <span className="visuallyHidden">
        {'Номер ячейки:'}
      </span>
      <span className={styles.prefix}>
        {'№'}
      </span>
      <input
        ref={inputRef}
        id={elementId}
        className={cn(styles.input, hasError && styles.invalid)}
        size={Math.max(localValue.length, 1)}
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </label>
  )
}

